import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money, d } from "@/api";
import { Badge, MarginTag, MODE_LABELS, STATUS_LABELS, DIRECTIONS, DIRECTION_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";

const STATES = ["taslak", "planlandi", "yolda", "aktarma", "varista", "teslim_edildi", "kapandi", "iptal", "arsiv"];
const EMPTY = {
  customer_id: "", direction: "export", mode: "kara", equipment: "20DC",
  origin: "", destination: "", cargo_desc: "",
  weight_kg: "", teu: "", vehicle_count: "", eta: "", bl_no: "", container_no: "",
  planned_cost_try: "", planned_sale_try: "", description: "", notes: "",
};
const EQUIPMENTS = ["20DC", "40HQ", "45HCPW", "40OT", "40FR", "Parsiyel", "Full", "Havayolu"];

export default function Operations() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const reqSeq = useRef(0);

  const load = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    return api
      .get("/operations", { params: { status: status || undefined } })
      .then((r) => { if (seq === reqSeq.current) { setRows(r.data); setLoadError(""); } })
      .catch((e) => { if (seq === reqSeq.current) setLoadError(errMsg(e)); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/parties", { params: { type: "musteri" } }).then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/operations", {
        ...form,
        weight_kg: Number(form.weight_kg || 0),
        teu: form.mode === "deniz" ? Number(form.teu || 0) : 0,
        vehicle_count: form.mode === "kara" ? Number(form.vehicle_count || 0) : 0,
        planned_cost_try: Number(form.planned_cost_try || 0),
        planned_sale_try: Number(form.planned_sale_try || 0),
        eta: form.eta || null,
      });
      toast.success("Operasyon dosyası oluşturuldu");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const filtered = rows.filter((r) =>
    !q || [r.no, r.customer_name, r.origin, r.destination, r.bl_no, r.container_no]
      .filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  );

  const exportCsv = () => {
    const head = ["Poz No", "Müşteri", "Çıkış", "Varış", "Mod", "Konşimento No", "Konteyner/Araç", "Durum", "Eksik Evrak", "Kâr TRY", "Marj %"];
    const lines = filtered.map((r) => [r.no, r.customer_name, r.origin, r.destination, r.mode, r.bl_no || "", r.container_no || "", r.status, r.missing_docs, r.profit_try, r.margin_pct]);
    const csv = [head, ...lines].map((l) => l.map((x) => `"${x}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "operasyonlar.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/operations/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "operasyonlar.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`${r.no} numaralı operasyon dosyası; taşıma ayakları, olayları, belgeleri ve masraflarıyla birlikte kalıcı olarak silinecek. Devam edilsin mi?`)) return;
    try {
      await api.delete(`/operations/${r.id}`);
      toast.success("Operasyon dosyası silindi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="operations-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Operasyon</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Operasyon Dosyaları</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("operation:write") && (
            <button className="brut-btn" onClick={() => setOpen(!open)} data-testid="new-operation-button">
              <Plus className="h-4 w-4" /> Yeni Operasyon
            </button>
          )}
          <button className="brut-btn-ghost" onClick={exportCsv} data-testid="export-operations-button">CSV Dışa Aktar</button>
          <button className="brut-btn-ghost" onClick={exportExcel} data-testid="export-operations-excel-button">Excel Dışa Aktar</button>
        </div>
      </div>

      {open && (
        <form onSubmit={save} className="brut grid animate-fade-up grid-cols-1 gap-3 p-4 sm:p-6 sm:grid-cols-3" data-testid="operation-form">
          <select required className="brut-input" value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="operation-customer-select">
            <option value="">Müşteri seçiniz</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="brut-input" value={form.direction}
            onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="operation-direction-select">
            {DIRECTIONS.map((x) => <option key={x} value={x}>{DIRECTION_LABELS[x]}</option>)}
          </select>
          <select className="brut-input" value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="operation-mode-select">
            <option value="kara">Kara</option><option value="deniz">Deniz</option><option value="hava">Hava</option>
          </select>
          <input type="date" className="brut-input" value={form.eta}
            onChange={(e) => setForm({ ...form, eta: e.target.value })} data-testid="operation-eta-input" />
          <select className="brut-input" value={form.equipment}
            onChange={(e) => setForm({ ...form, equipment: e.target.value })} data-testid="operation-equipment-select">
            {EQUIPMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input placeholder="Konşimento (BL) No" className="brut-input" value={form.bl_no}
            onChange={(e) => setForm({ ...form, bl_no: e.target.value })} data-testid="operation-blno-input" />
          <input placeholder="Konteyner / Araç No" className="brut-input" value={form.container_no}
            onChange={(e) => setForm({ ...form, container_no: e.target.value })} data-testid="operation-containerno-input" />
          <input required placeholder="Çıkış" className="brut-input" value={form.origin}
            onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="operation-origin-input" />
          <input required placeholder="Varış" className="brut-input" value={form.destination}
            onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="operation-destination-input" />
          <input required placeholder="Yük açıklaması" className="brut-input" value={form.cargo_desc}
            onChange={(e) => setForm({ ...form, cargo_desc: e.target.value })} data-testid="operation-cargo-input" />
          <input type="number" step="0.01" placeholder="Ağırlık (kg)" className="brut-input" value={form.weight_kg}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="operation-weight-input" />
          {form.mode === "deniz" && (
            <input type="number" step="0.1" placeholder="TEU (konteyner eşdeğeri)" className="brut-input" value={form.teu}
              onChange={(e) => setForm({ ...form, teu: e.target.value })} data-testid="operation-teu-input" />
          )}
          {form.mode === "kara" && (
            <input type="number" step="1" placeholder="Araç adedi" className="brut-input" value={form.vehicle_count}
              onChange={(e) => setForm({ ...form, vehicle_count: e.target.value })} data-testid="operation-vehiclecount-input" />
          )}
          <input type="number" step="0.01" placeholder="Planlanan maliyet (TRY)" className="brut-input" value={form.planned_cost_try}
            onChange={(e) => setForm({ ...form, planned_cost_try: e.target.value })} data-testid="operation-cost-input" />
          <input type="number" step="0.01" placeholder="Planlanan satış (TRY)" className="brut-input" value={form.planned_sale_try}
            onChange={(e) => setForm({ ...form, planned_sale_try: e.target.value })} data-testid="operation-sale-input" />
          <textarea rows={3} placeholder="Açıklama (operasyona ilişkin)" className="brut-input sm:col-span-3" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="operation-description-input" />
          <textarea rows={3} placeholder="Not (iç ekip notu)" className="brut-input sm:col-span-3" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="operation-notes-input" />
          <button className="brut-btn sm:col-span-3" disabled={busy} data-testid="operation-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Operasyon Dosyası Aç
          </button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select className="brut-input max-w-[220px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="operation-status-filter">
          <option value="">Tüm durumlar</option>
          {STATES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <input className="brut-input max-w-[280px]" placeholder="Poz no, BL, konteyner, müşteri, rota ara..." value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="operation-search-input" />
        <div className="flex border border-zinc-300">
          <button type="button" onClick={() => setStatus("")} data-testid="operation-view-all"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${status === "" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Tümü
          </button>
          <button type="button" onClick={() => setStatus("arsiv")} data-testid="operation-view-archived"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${status === "arsiv" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Arşiv
          </button>
          <button type="button" onClick={() => setStatus("iptal")} data-testid="operation-view-cancelled"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${status === "iptal" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            İptal
          </button>
        </div>
        <span className="tnum font-mono text-xs text-zinc-500" data-testid="operation-count">{filtered.length} dosya</span>
      </div>

      <div className="brut overflow-x-auto" data-testid="operations-table">
        <table className="dense w-full">
          <thead>
            <tr><th>Poz No</th><th>Müşteri</th><th>Rota</th><th>Mod</th><th>Konşimento No</th><th>Konteyner/Araç</th><th>Durum</th><th>ETA</th>
              <th className="!text-right">Eksik Evrak</th><th className="!text-right">Kâr</th><th className="!text-right">Marj</th><th className="!text-right">İşlem</th></tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className={r.is_delayed ? "bg-red-50" : i % 2 ? "bg-zinc-50" : ""} data-testid={`operation-row-${r.id}`}>
                <td>
                  <Link to={`/operasyonlar/${r.id}`} className="font-mono font-semibold text-klein hover:underline"
                    data-testid={`operation-link-${r.id}`}>{r.no}</Link>
                </td>
                <td>{r.customer_name}</td>
                <td className="text-xs">{r.origin} → {r.destination}</td>
                <td>{MODE_LABELS[r.mode]}</td>
                <td className="font-mono text-xs">{r.bl_no || "-"}</td>
                <td className="font-mono text-xs">{r.container_no || "-"}</td>
                <td><Badge value={r.status} /></td>
                <td className="text-xs">{d(r.eta)}</td>
                <td className={`tnum text-right font-mono ${r.missing_docs ? "text-yellow-700" : "text-zinc-400"}`}>{r.missing_docs}</td>
                <td className="tnum text-right font-mono">{money(r.profit_try)}</td>
                <td className="text-right"><MarginTag pct={r.margin_pct} /></td>
                <td className="text-right">
                  {can("*") && ["arsiv", "iptal"].includes(r.status) && (
                    <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600"
                      data-testid={`delete-operation-${r.id}`} title="Kalıcı olarak sil">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="py-10 text-center text-zinc-500" data-testid="operations-empty-state">
                {loading
                  ? "Yükleniyor..."
                  : loadError
                    ? loadError
                    : "Henüz operasyon kaydı yok. 'Yeni Operasyon' ile dosya açabilir veya onaylanan teklifle otomatik açılmasını sağlayabilirsiniz."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
